import {
  ChangeDetectionStrategy, Component, ElementRef, OnDestroy, PLATFORM_ID, ViewChild,
  effect, inject, input, signal, untracked,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { OneButtonComponent } from '../../one-button/one-button.component';
import { OneCheckChipComponent } from '../../one-check-chip/one-check-chip.component';
import { AiAgentService } from '../../../services/API/ai-agent.service';
import { AudioCaptureService } from '../../../services/audio-capture.service';
import { TourService } from '../../../services/tour-mode/tour.service';
import { TourRoteiroService } from '../../../services/tour-mode/tour-roteiro.service';
import { GuiaOneService } from '../../../services/tour-mode/guia-one.service';
import { ScreenContextService } from '../../../services/screen-context.service';
import { AiRealceService } from '../../../services/ai-realce/ai-realce.service';
import { RoutesService } from '../../../../routes.service';
import { Acao, AlertaAssistente, AiOpcao, AiResultadoItem, AiResultados, AlvoRealce, AiMessage } from '../ai-agent.models';
import { renderizarSegmentos, SegmentoTexto } from '../ai-markdown.util';
import { GuiaChipsComponent } from '../guia-chips.component';
import { ROTAS_CONHECIDAS } from '../ai-rotas-conhecidas';

/**
 * Corpo da conversa do assistente (spec §4.1): centro de alertas, bolhas, chips, input estilo
 * WhatsApp, gravação e despacho das ações do backend. Hospedado pelas cascas
 * `ai-assistant-overlay` (palco / chat flutuante) e `ai-painel` (coluna acoplada) — por isso
 * nenhum código aqui fecha a casca direto: fechar é sempre `svc.fechar()`.
 */
@Component({
  selector: 'ai-conversa',
  standalone: true,
  imports: [
    CommonModule, FormsModule, MatIconModule, OneButtonComponent, OneCheckChipComponent,
    GuiaChipsComponent,
  ],
  templateUrl: './ai-conversa.component.html',
  styleUrl: './ai-conversa.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class.compacto]': 'compacto()' },
})
export class AiConversaComponent implements OnDestroy {
  /** true em chat flutuante e painel: indicador "pensando" e margens compactas. */
  readonly compacto = input<boolean>(false);

  readonly svc = inject(AiAgentService);
  private readonly captura = inject(AudioCaptureService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly routes = inject(RoutesService);
  private readonly tour = inject(TourService);
  /** Público: o template lê `tourRoteiro.ativo()` para esconder o chip D68 durante o tour. */
  readonly tourRoteiro = inject(TourRoteiroService);
  private readonly screenContext = inject(ScreenContextService);
  private readonly realce = inject(AiRealceService);
  /** D67/D68/D71 — dono das ações do orbe (chips) e do chip "Ver no tour desta tela". */
  readonly guia = inject(GuiaOneService);

  private static readonly ITEM_CHAVE = /^item:(\d+):(.+)$/;

  /** Quebra o texto da bolha em segmentos seguros (texto/link/negrito) para o template. */
  segmentos(texto: string): SegmentoTexto[] {
    return renderizarSegmentos(texto);
  }

  /** Alvos do(s) `realcar` de uma mensagem do assistente, ou null quando não há (spec §6.3b). */
  alvosDe(m: AiMessage): AlvoRealce[] | null {
    const alvos = (m.acoes ?? [])
      .filter((a) => a.tipo === 'realcar')
      .flatMap((a) => a.alvos ?? []);
    return alvos.length > 0 ? alvos : null;
  }

  /** Reacende os alvos de uma bolha já respondida — chip "Ver na tela". */
  verNaTela(alvos: AlvoRealce[]): void {
    this.realce.acender(alvos);
  }

  /** Mapeia as chaves `aplicados` de {@link ResultadoAplicar} para alvos de realce: chaves
   *  `item:<n>:<campo>` viram alvo de item, o restante vira alvo de campo do cabeçalho. */
  private static alvosDeAplicados(aplicados: string[]): AlvoRealce[] {
    return aplicados.map((chave) => {
      const m = AiConversaComponent.ITEM_CHAVE.exec(chave);
      return m
        ? { tipo: 'item' as const, nome: m[2], item: Number(m[1]) }
        : { tipo: 'campo' as const, nome: chave };
    });
  }

  /** Auto-scroll da conversa: enquanto o usuário está no fim (folga de {@link FOLGA_FIM_PX}),
   *  cada mensagem nova cola a rolagem na última; rolagem manual para cima DESLIGA o seguir, e
   *  voltar ao fim religa. */
  readonly seguindoUltima = signal(true);
  private static readonly FOLGA_FIM_PX = 48;
  @ViewChild('conversa') private conversaRef?: ElementRef<HTMLElement>;
  @ViewChild('inputTexto') private inputRef?: ElementRef<HTMLInputElement>;

  constructor() {
    // Despacha ações do backend sempre que ultimasAcoes mudar após uma resposta. Consome
    // (zera) ANTES de despachar — mesmo padrão do prefillPendente logo abaixo: a conversa é
    // destruída/recriada a cada abrir/fechar e dock/undock, e `ultimasAcoes` só é setado pelo
    // serviço, nunca limpo. Sem o consumo, o effect do construtor de uma conversa RECÉM-CRIADA
    // reexecutava o `redirect`/`tour_step`/`colapsar_chat` da resposta anterior (C1).
    effect(() => {
      const acoes = this.svc.ultimasAcoes();
      if (acoes.length === 0) return;
      untracked(() => this.svc.ultimasAcoes.set([]));
      for (const acao of acoes) {
        this.executarAcao(acao);
      }
    });
    // Bolha nova na conversa → rola para o fim, respeitando o seguir. O setTimeout deixa o
    // Angular renderizar a bolha antes de medir o scrollHeight.
    effect(() => {
      this.svc.mensagens();
      if (!isPlatformBrowser(this.platformId)) return;
      setTimeout(() => this.rolarParaFim());
    });
    // Abrir sempre aterrissa na última mensagem (antes vivia no `abrir()` do overlay; aqui vale
    // para qualquer casca, inclusive a que mantém a conversa montada com o chat fechado).
    effect(() => {
      if (this.svc.aberto()) this.seguindoUltima.set(true);
    });
    // A casca fecha só com `svc.fechar()` — nenhuma delas sabe da gravação —, então a devolução
    // do microfone ao fechar o chat (antes no `fechar()` do overlay) mora aqui. Vale para a casca
    // que MANTÉM a conversa montada (ai-painel); quando a casca destrói a conversa (overlay, com
    // o `@if (aberto())`), quem fecha o ciclo é o ngOnDestroy — os dois chamam `pararGravacao`,
    // que é idempotente (zera `manual`/`gravando` antes do primeiro await).
    effect(() => {
      if (!this.svc.aberto() && this.gravando()) void this.pararGravacao();
    });
    // Prefill (search-bar da Home): o Subject não tem replay e a emissão acontece no MESMO tick do
    // `abrir()`, antes de este componente existir — por isso o gatilho é o signal pendente.
    effect(() => {
      const valor = this.svc.prefillPendente();
      if (valor === null) return;
      this.texto.set(valor);
      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          const el = this.inputRef?.nativeElement;
          if (el) {
            el.focus();
            const len = el.value.length;
            el.setSelectionRange(len, len);
          }
        }, 50);
      }
      // Consome: sem isto o mesmo prefill voltaria a cada remontagem da conversa.
      untracked(() => this.svc.prefillPendente.set(null));
    });
  }

  onScrollConversa(ev: Event): void {
    const el = ev.target as HTMLElement;
    this.seguindoUltima.set(
      el.scrollHeight - el.scrollTop - el.clientHeight <= AiConversaComponent.FOLGA_FIM_PX);
  }

  private rolarParaFim(): void {
    const el = this.conversaRef?.nativeElement;
    if (el && this.seguindoUltima()) el.scrollTop = el.scrollHeight;
  }

  ngOnDestroy(): void {
    // Idempotente: só mexe na reserva 'manual' se ela de fato foi tomada (gravando() true).
    // Uma reserva vazada aqui mantém o microfone aberto E silencia o modo conversa para sempre
    // (ver AudioCaptureService.despachar: eventos ficam suprimidos enquanto 'manual' segue
    // reservado) — e a conversa VIVE dentro de um @if na casca (overlay), então este destroy é o
    // caminho REAL de "fechar o chat gravando". Delega ao `pararGravacao` justamente para ENVIAR
    // o áudio (comportamento do antigo `fechar()` do overlay): descartar aqui perderia a gravação
    // e ainda deixaria `svc.estado` travado em 'ouvindo'.
    if (this.gravando()) {
      void this.pararGravacao();
    }
  }

  /** Texto do input. Signal (e não campo simples) porque o componente é OnPush. */
  readonly texto = signal('');
  readonly gravando = signal(false);
  /** Segundos decorridos da gravação em curso — o timer da barra estilo WhatsApp. */
  readonly segundosGravacao = signal(0);
  /** Waveform rolante da gravação: janela das últimas leituras de volume (0..1), estilo WhatsApp. */
  readonly ondas = signal<number[]>(AiConversaComponent.ondasVazias());
  private static readonly NUM_ONDAS = 28;
  private static ondasVazias(): number[] {
    return new Array<number>(AiConversaComponent.NUM_ONDAS).fill(0);
  }
  private timerGravacao: ReturnType<typeof setInterval> | null = null;
  private timerOnda: ReturnType<typeof setInterval> | null = null;
  private manual: { parar: () => Promise<Blob> } | null = null;

  /** Formata o timer da gravação como m:ss (ex.: 0:07, 1:15). */
  tempoGravacao(): string {
    const total = this.segundosGravacao();
    const min = Math.floor(total / 60);
    const seg = (total % 60).toString().padStart(2, '0');
    return `${min}:${seg}`;
  }

  private iniciarTimerGravacao(): void {
    this.segundosGravacao.set(0);
    this.ondas.set(AiConversaComponent.ondasVazias());
    this.timerGravacao = setInterval(() => this.segundosGravacao.update(v => v + 1), 1000);
    // Waveform: desliza a janela a cada 100 ms com o volume atual do microfone. `nivel` já é
    // publicado pelo AudioCaptureService no laço de decisão (20 ms) — aqui só amostramos.
    this.timerOnda = setInterval(
      () => this.ondas.update(a => [...a.slice(1), this.captura.nivel()]),
      100,
    );
  }

  private pararTimerGravacao(): void {
    if (this.timerGravacao) {
      clearInterval(this.timerGravacao);
      this.timerGravacao = null;
    }
    if (this.timerOnda) {
      clearInterval(this.timerOnda);
      this.timerOnda = null;
    }
  }

  escolherAlerta(alerta: AlertaAssistente): void {
    void this.svc.enviarPerguntaDoAlerta(alerta);
  }

  dispensarAlertaCentro(id: number, ev: Event): void {
    ev.stopPropagation();
    void this.svc.dispensarAlerta(id);
  }

  enviar(e: Event): void {
    e.preventDefault();
    // Trava anti-pergunta-dupla: não limpa o texto — o usuário não perde o que digitou.
    if (this.svc.ocupado()) return;
    const t = this.texto();
    if (!t.trim()) return;
    this.texto.set('');
    this.svc.converseTexto(t);
  }

  escolher(o: AiOpcao): void {
    // Desambiguação instantânea: chip com ação embutida navega em 1 toque, sem round-trip ao backend.
    if (o.acao) {
      this.executarAcao(o.acao);
      this.svc.fechar();
      return;
    }
    // envia o ID (resolve sem re-buscar) e exibe o rótulo na conversa
    this.svc.converseTexto(o.id, o.rotulo);
  }

  confirmar(token: string): void {
    this.svc.confirmar(token);
  }

  abrirItem(it: AiResultadoItem): void {
    if (it.rota) {
      this.router.navigate([it.rota]);
      this.svc.fechar();
    }
  }

  verTodos(r: AiResultados): void {
    if (r.rotaUi) {
      this.router.navigate([r.rotaUi], { queryParams: r.filtros ?? {} });
      this.svc.fechar();
    }
  }

  /**
   * Despacha uma ação estruturada recebida do backend.
   * SECURITY: redirecionamentos só ocorrem para rotas na allowlist `ROTAS_CONHECIDAS`.
   * Nunca usa window.location / href com URL do backend.
   */
  executarAcao(acao: Acao): void {
    switch (acao.tipo) {
      case 'redirect':
        if (acao.rota) void this.navegarSeguro(acao.rota, undefined, 'redirect');
        break;
      case 'redirect_com_filtros':
        if (acao.rota) void this.navegarSeguro(acao.rota, acao.filtros, 'redirect_com_filtros');
        break;
      case 'redirect_subperfil': {
        // O front resolve a URL real a partir das permissões do usuário (pessoa DB) — nunca uma rota
        // do backend —, então a allowlist ROTAS_CONHECIDAS não se aplica aqui. O retorno booleano
        // fecha o descarte silencioso (subperfil sem rota no token — H2 da investigação 2026-08-18).
        if (acao.subperfil == null || !acao.acao) break;
        // Guarda de form sujo (mesma do navegarSeguro): chips com ação embutida executam
        // client-side sem passar pelo backend na hora do clique — sem esta checagem o
        // redirect_subperfil destruía o trabalho não salvo (gap conhecido da Área F).
        if (this.screenContext.formSujo()) {
          this.svc.avisoEfemero.set(
            'Você tem alterações não salvas nesta tela, então não naveguei. Salve ou descarte e me chame de novo.');
          this.svc.reportarAcao('redirect_subperfil', null, 'bloqueada_form_sujo', acao.subperfil);
          break;
        }
        let navegou = false;
        if (acao.acao === 'lista') navegou = this.routes.navigateToSubperfil(acao.subperfil);
        else if (acao.acao === 'novo') navegou = this.routes.navigateToSubperfilNovo(acao.subperfil, { prefill: acao.prefill });
        else if (acao.acao === 'editar' && acao.entidadeId)
          navegou = this.routes.navigateToSubperfilEditar(acao.subperfil, acao.entidadeId, { prefill: acao.prefill });
        this.svc.reportarAcao('redirect_subperfil', null,
          navegou ? 'executada' : 'subperfil_nao_resolvido', acao.subperfil);
        break;
      }
      case 'tour_step':
        void this.executarTourStep(acao);
        break;
      case 'colapsar_chat':
        this.svc.fechar();
        break;
      case 'preencher_campo': {
        // Área F / preenchimento contínuo: escreve no formulário vivo registrado no
        // ScreenContextService, sem navegar. A guarda dura (nunca sobrescrever campo com
        // valor) mora no aplicarCampos — o aviso diferencia recusa de falha.
        const r = this.screenContext.aplicarCampos(acao.filtros ?? {});
        if (r.aplicados.length > 0) this.realce.acender(AiConversaComponent.alvosDeAplicados(r.aplicados));
        if (r.jaPreenchidos.length > 0) {
          const nomes = r.jaPreenchidos.map(c => c.replace(/^item:\d+:/, '')).join(', ');
          this.svc.avisoEfemero.set(
            `Não alterei ${r.jaPreenchidos.length > 1 ? 'os campos' : 'o campo'} ${nomes} — ` +
            'já preenchido na tela. Ajuste manualmente se quiser trocar.');
        } else if (r.aplicados.length === 0) {
          this.svc.avisoEfemero.set('Não consegui preencher isso na tela atual.');
        }
        const resultado = r.aplicados.length > 0
          ? 'executada'
          : r.jaPreenchidos.length > 0 ? 'bloqueada_ja_preenchido' : 'erro';
        this.svc.reportarAcao('preencher_campo', null, resultado);
        break;
      }
      case 'selecionar_aba': {
        // Preenchimento contínuo: troca de aba NA tela aberta. O selecionarAba re-checa o
        // bloqueio ao vivo (wizard) — aba disabled NUNCA é forçada.
        const nomeAba = acao.filtros?.['aba'] ?? '';
        const ok = !!nomeAba && this.screenContext.selecionarAba(nomeAba);
        if (ok) this.realce.acender([{ tipo: 'aba', nome: nomeAba }]);
        if (!ok) {
          this.svc.avisoEfemero.set(
            `A aba "${nomeAba}" está bloqueada até completar a etapa atual.`);
        }
        this.svc.reportarAcao('selecionar_aba', null, ok ? 'executada' : 'bloqueada_aba');
        break;
      }
      case 'realcar': {
        // Referências marcadas pela IA (tool apontar_na_tela). Alvo sem âncora é ignorado em
        // silêncio (spec §6.2) — não é falha do usuário nem do sistema.
        const n = this.realce.acender(acao.alvos ?? []);
        this.svc.reportarAcao('realcar', null, n > 0 ? 'executada' : 'erro');
        break;
      }
      // 'opcoes' e 'confirm' são tratados via UI (chips / botão Confirmar)
    }
  }

  /**
   * Navega primeiro e só inicia o tour depois de a navegação resolver: os tour-steps
   * da tela nova se registram no ngOnInit — disparar no mesmo tick acendia passo da
   * tela antiga (casamento por índice) ou nenhum.
   */
  private async executarTourStep(acao: Acao): Promise<void> {
    if (acao.rota) {
      const navegou = await this.navegarSeguro(acao.rota, undefined, 'tour_step');
      if (!navegou) return;
    }
    if (acao.tourSlug) {
      setTimeout(() => this.tour.startFromSlug(acao.tourSlug!), 150);
      return;
    }
    // Sem slug = "me mostra o sistema": dispara o roteiro guiado inteiro.
    //
    // As três guardas abaixo existem porque `iniciar()` ZERA o índice: quem estava no passo 7
    // e pedisse "me mostra o sistema" perdia todo o progresso em silêncio — e o sensor e o
    // listener daquele passo sobreviviam ao await da persistência, podendo chamar `proximo()`
    // sobre o índice já zerado (a corrida que o motor também fecha na origem, com
    // `pararEspera()` no topo de `iniciar()`).
    if (this.tourRoteiro.ativo()) {
      // Já está rodando: fecha o chat para o usuário ver o tour, e NÃO reinicia nada.
      this.svc.fechar();
      return;
    }
    if (!this.tourRoteiro.elegivel()) {
      // Nenhuma tela do roteiro é acessível a este usuário: o assistente responde normalmente
      // em vez de abrir um tour que não teria o que mostrar.
      return;
    }
    this.svc.fechar();
    // Progresso guardado: continuar de onde parou, nunca recomeçar do zero.
    if (this.tourRoteiro.temTourPendente()) {
      await this.tourRoteiro.retomar();
      return;
    }
    await this.tourRoteiro.iniciar();
  }

  /**
   * Navega via Angular Router apenas para rotas presentes na allowlist.
   * Rotas fora da allowlist são silenciosamente ignoradas.
   * NUNCA usa href/window.location com valor fornecido pelo backend.
   */
  private async navegarSeguro(
    rota: string, filtros?: Record<string, string>, tipoAcao: string = 'redirect'): Promise<boolean> {
    const base = rota.split('?')[0];
    const permitida = [...ROTAS_CONHECIDAS].some(r => base === r || base.startsWith(r + '/'));
    if (!permitida) {
      // Antes descartava em silêncio — o backend achava que navegou (telemetria fecha o loop).
      this.svc.reportarAcao(tipoAcao, base, 'bloqueada_allowlist');
      return false;
    }
    // Form vivo com trabalho não salvo: navegar para outra tela destruiria o que o
    // usuário digitou — bloqueia e avisa (mesma rota passa: deep-link/prefill na própria tela).
    const rotaAtual = this.router.url.split('?')[0];
    if (base !== rotaAtual && this.screenContext.formSujo()) {
      this.svc.avisoEfemero.set(
        'Você tem alterações não salvas nesta tela, então não naveguei. Salve ou descarte e me chame de novo.');
      this.svc.reportarAcao(tipoAcao, base, 'bloqueada_form_sujo');
      return false;
    }
    const ok = await this.router.navigate([base], filtros ? { queryParams: filtros } : undefined);
    // null = navegação ignorada (já estava na rota) — conta como sucesso para o tour.
    this.svc.reportarAcao(tipoAcao, base, ok !== false ? 'executada' : 'erro');
    return ok !== false;
  }

  async alternarMic(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.gravando()) { await this.pararGravacao(); return; }
    // Não abre gravação enquanto a resposta anterior não chegou (mesma trava do texto).
    if (this.svc.ocupado()) return;

    // Push-to-talk usa o MESMO stream do modo conversa — nada de segundo getUserMedia — e reserva
    // o microfone como dono 'manual'. Sem callback de propósito: o botão marca início e fim, e um
    // callback aqui sobrescreveria o do modo conversa (era assim que a conversa morria calada).
    const ok = await this.captura.abrir('manual');
    if (!ok) {
      this.gravando.set(false);
      this.svc.estado.set('ocioso');
      return;
    }
    this.manual = this.captura.capturaManual();
    this.gravando.set(true);
    this.iniciarTimerGravacao();
    this.svc.estado.set('ouvindo');
  }

  /** Envia o áudio gravado (botão ✈ da barra de gravação — para e envia). */
  async finalizarGravacao(): Promise<void> {
    await this.pararGravacao();
  }

  /** Descarta a gravação (lixeira, estilo WhatsApp): devolve o microfone e NÃO envia nada. */
  cancelarGravacao(): void {
    this.pararTimerGravacao();
    this.manual = null;
    this.gravando.set(false);
    this.captura.fechar('manual');
    this.svc.estado.set('ocioso');
  }

  private async pararGravacao(): Promise<void> {
    this.pararTimerGravacao();
    const manual = this.manual;
    this.manual = null;
    this.gravando.set(false);
    if (!manual) {
      this.captura.fechar('manual');
      this.svc.estado.set('ocioso');
      return;
    }

    let wav: Blob | null = null;
    try {
      // Extrai ANTES de devolver o microfone: fechar() derruba o grafo e o ring buffer com ele.
      wav = await manual.parar();
    } finally {
      // Devolver a reserva é OBRIGATÓRIO: sem isto o stream, o AudioContext, o worklet e o
      // setInterval de 20 ms (50 nivel.set()/s → change detection global) ficavam vivos
      // indefinidamente, com o indicador de microfone aceso no navegador para sempre.
      // `fechar('manual')` não derruba o modo conversa se ele já estava ligado.
      this.captura.fechar('manual');
    }
    if (wav && wav.size > 44) await this.svc.enviarVoz(wav);
    else this.svc.estado.set('ocioso');
  }
}
